import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type UseFormReturn, type Resolver } from 'react-hook-form';
import moment from 'moment-timezone';
import { useFormMethods, useStepper } from '@hqo/multi-step-form';
import type { OwnerParams } from 'shared/types';
import { useTimeZone } from 'shared/hooks';
import { cancelVisit, createVisit, updateVisit, updateVisitSeries } from 'store/visits/actions';
import { createVisitLoadingState } from 'store/visits/selectors';
import type { CreateVisitDto, UpdateVisitSeriesPayload } from 'store/visits/types';
import { datesToDateOnlyStrings, getEarliestVisitDate } from './utils/visit-dates.utils';
import { getEffectiveEndTime } from './steps/when-step/when-step.utils';
import { currentUser } from 'store/user/selectors';
import { WhenStep } from './steps/when-step';
import { HostStep } from './steps/host-step';
import { VisitStep } from './steps/visit-step';
import { useStepperInstance } from './hooks/use-stepper-instance.hook';
import { CreateVisitFormV2Provider, useCreateVisitFormV2Context } from './context/create-visit-form-v2.context';
import { useVisitStepFieldsConfig } from './hooks/use-ui-metadata-field-config.hook';
import { useIncludeHostStep } from './hooks/use-include-host-step.hook';
import {
  CreateVisitFormV2Values,
  HostingType,
  MonthlyMode,
  RepeatType,
  Visitor,
  VisitorMode,
} from './types/form-data.types';
import { createVisitFormV2Schema } from './create-visit-form-v2.schema';
import { useBuildingSettings } from 'hooks/use-building-settings.hook';
import { useBuildingData } from 'store/building/hooks';
import { getFloorSuiteOptions } from './utils/get-floor-suite-options';
import { useFlag } from 'hooks/use-flag.hook';
import { useFieldMetadata } from 'store/ui-metadata/hooks';
import { LDFlags } from 'shared/consts';
import { VisitorFrequencyEnum } from 'components/visit-form/types';
import { didPrimaryVisitorEmailOrPhoneChange } from './primary-visitor-contact.util';
import { useCheckInOptionsData } from 'components/visit-form/steps/visitor-step/visitor-form/use-check-in-options-data.hook';
import { useIsActivityPane } from 'components/activity-pane-provider';
import { selectDefaultCheckInOptionByUuidSelector } from 'store/building-settings/selectors';
import { resolveDefaultVisitType } from './steps/visit-step/components/check-in-type/resolve-default-visit-type';

const EMPTY_VISITOR: Visitor = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  photoUuid: '',
};

const getSelectedVisitDates = (formValues: CreateVisitFormV2Values): Date[] => {
  if (formValues.isMultiDay && formValues.visitDates && formValues.visitDates.length > 0) {
    return formValues.visitDates;
  }
  if (formValues.visitStartDate) {
    return [formValues.visitStartDate];
  }
  return [];
};

const mapVisitorsToSeriesPayload = (visitors: Visitor[]): NonNullable<UpdateVisitSeriesPayload['visitors']> =>
  visitors.map((visitor) => ({
    firstName: visitor.firstName,
    lastName: visitor.lastName,
    ...(visitor.email ? { email: visitor.email } : {}),
    ...(visitor.phone ? { phone: visitor.phone } : {}),
  }));

const parseTime = (timeString: string): [number, number] => {
  const [hoursString, minutesString] = timeString.split(':');

  return [parseInt(hoursString, 10), parseInt(minutesString, 10)];
};

const getDateTime = (date: Date, time: string, timeZone: string): Date => {
  const [hour, minute] = parseTime(time);

  return moment.tz([date.getFullYear(), date.getMonth(), date.getDate(), hour, minute], timeZone).toDate();
};

const WEEKDAY_KEYS: Array<keyof NonNullable<CreateVisitDto['recurrence_days']>> = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
];
const WEEKEND_KEYS: Array<keyof NonNullable<CreateVisitDto['recurrence_days']>> = ['saturday', 'sunday'];
const ALL_DAY_KEYS: Array<keyof NonNullable<CreateVisitDto['recurrence_days']>> = [...WEEKDAY_KEYS, ...WEEKEND_KEYS];

const isDayEnabled = (
  recurrenceDays: CreateVisitDto['recurrence_days'] | undefined,
  dayKey: keyof NonNullable<CreateVisitDto['recurrence_days']>,
): boolean => recurrenceDays?.[dayKey] === true;

const mapCustomRecurrenceToFrequency = (recurrenceDays?: CreateVisitDto['recurrence_days']): VisitorFrequencyEnum => {
  if (ALL_DAY_KEYS.every((dayKey) => isDayEnabled(recurrenceDays, dayKey))) {
    return VisitorFrequencyEnum.DAILY;
  }
  if (
    WEEKDAY_KEYS.every((dayKey) => isDayEnabled(recurrenceDays, dayKey)) &&
    WEEKEND_KEYS.every((dayKey) => !isDayEnabled(recurrenceDays, dayKey))
  ) {
    return VisitorFrequencyEnum.WORK_DAYS;
  }
  return VisitorFrequencyEnum.WEEKLY;
};

const mapRepeatTypeToFrequency = (
  repeatType: RepeatType,
  recurrenceDays?: CreateVisitDto['recurrence_days'],
): VisitorFrequencyEnum => {
  switch (repeatType) {
    case RepeatType.NONE:
      return VisitorFrequencyEnum.ONCE;
    case RepeatType.DAILY:
      return VisitorFrequencyEnum.DAILY;
    case RepeatType.WEEKDAY:
      return VisitorFrequencyEnum.WORK_DAYS;
    case RepeatType.WEEKLY:
      return VisitorFrequencyEnum.WEEKLY;
    case RepeatType.MONTHLY_DAY_OF_MONTH:
    case RepeatType.MONTHLY_DAY_OF_WEEK:
      return VisitorFrequencyEnum.MONTHLY;
    case RepeatType.CUSTOM:
      return mapCustomRecurrenceToFrequency(recurrenceDays);
    default:
      return VisitorFrequencyEnum.ONCE;
  }
};

const mapRepeatTypeToMonthlyMode = (repeatType: RepeatType): MonthlyMode | undefined => {
  if (repeatType === RepeatType.MONTHLY_DAY_OF_MONTH) {
    return MonthlyMode.DAY_OF_MONTH;
  }
  if (repeatType === RepeatType.MONTHLY_DAY_OF_WEEK) {
    return MonthlyMode.DAY_OF_WEEK;
  }
  return undefined;
};

const CreateVisitFormV2Content = ({
  Stepper,
  isSubmittingVisit,
  isCreatingVisit,
  includeHostStep,
}: {
  Stepper: ReturnType<typeof useStepperInstance>['Stepper'];
  isSubmittingVisit: boolean;
  isCreatingVisit: boolean;
  includeHostStep: boolean;
}): JSX.Element => {
  const { formatMessage } = useIntl();
  const { isEditMode } = useCreateVisitFormV2Context();
  const stepper = useStepper();
  const formMethods = useFormMethods() as unknown as UseFormReturn<CreateVisitFormV2Values> | undefined;
  const isSubmitting = formMethods?.formState.isSubmitting ?? false;
  const hasInitializedEditMode = useRef(false);

  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    if (hasInitializedEditMode.current) {
      return;
    }
    hasInitializedEditMode.current = true;

    stepper.all.forEach((step) => {
      stepper.setMetadata(step.id, { isVisited: true });
    });
  }, [isEditMode, stepper]);

  return (
    <div data-cy="create-visit-form-v2">
      <Stepper.Navigation>
        <Stepper.NavStep of="when">
          {formatMessage({
            id: 'createVisitFormV2.steps.when',
            defaultMessage: 'When',
          })}
        </Stepper.NavStep>
        {includeHostStep && (
          <Stepper.NavStep of="host">
            {formatMessage({
              id: 'createVisitFormV2.steps.host',
              defaultMessage: 'Host',
            })}
          </Stepper.NavStep>
        )}
        <Stepper.NavStep of="visit">
          {formatMessage({
            id: 'createVisitFormV2.steps.visit',
            defaultMessage: 'Visitors',
          })}
        </Stepper.NavStep>
      </Stepper.Navigation>

      <Stepper.Panel of="when">
        <WhenStep />
      </Stepper.Panel>
      {includeHostStep && (
        <Stepper.Panel of="host">
          <HostStep />
        </Stepper.Panel>
      )}
      <Stepper.Panel of="visit">
        <VisitStep />
      </Stepper.Panel>

      <Stepper.Controls className="mt-4 sm:mt-6">
        <Stepper.PreviousButton>
          {formatMessage({
            id: 'buttons.back',
            defaultMessage: 'Back',
          })}
        </Stepper.PreviousButton>
        <Stepper.NextButton disabled={isSubmitting}>
          {formatMessage({
            id: 'buttons.continue',
            defaultMessage: 'Continue',
          })}
        </Stepper.NextButton>
        <Stepper.SubmitButton
          disabled={isSubmitting || isSubmittingVisit || isCreatingVisit}
          loading={isSubmittingVisit || isCreatingVisit}
        >
          {isEditMode
            ? formatMessage({
                id: 'buttons.updateVisit',
                defaultMessage: 'Update visit',
              })
            : formatMessage({
                id: 'buttons.addVisit',
                defaultMessage: 'Add visit',
              })}
        </Stepper.SubmitButton>
      </Stepper.Controls>
    </div>
  );
};

const CreateVisitFormV2Inner = (): JSX.Element => {
  const dispatch = useDispatch();
  const { ownerId: buildingUuid, visitId } = useParams<OwnerParams & { visitId?: string }>();
  const isActivityPane = useIsActivityPane();
  const timeZone = useTimeZone();
  const { settings: buildingSettings } = useBuildingSettings();
  const showUiMetadata = useFlag(LDFlags.SHOW_VISIT_FORM_UI_METADATA, false);
  const customFormDefinition = useFieldMetadata('custom_fields');
  const hasCustomFields = !!customFormDefinition;
  const visitFieldsConfig = useVisitStepFieldsConfig();
  const includeHostStep = useIncludeHostStep(visitFieldsConfig);
  const user = useSelector(currentUser);
  const isCreatingVisit = useSelector(createVisitLoadingState);
  const visitContext = useCreateVisitFormV2Context();
  const {
    isEditMode,
    getVisitStepData,
    getWhenStepData,
    getHostStepData,
    editDataVersion,
    baselinePrimaryVisitorContact,
    seriesUuid,
    originalSeriesDates,
  } = visitContext;

  const savedWhenData = getWhenStepData();
  const savedHostData = getHostStepData();
  const savedVisitData = getVisitStepData();

  const stepperOptions = {
    skipVisitValidation: false,
    acknowledgmentRequired: !!visitFieldsConfig.acknowledgmentText,
    includeHostStep,
  };
  const stepperInstance = useStepperInstance(stepperOptions);

  const today = useMemo(() => new Date(), []);
  const buildingDefaultStartTime = showUiMetadata ? buildingSettings?.defaultVisitStartTime ?? null : null;
  const buildingDefaultEndTime = showUiMetadata ? buildingSettings?.defaultVisitEndTime ?? null : null;
  const defaultStartTime = useMemo(() => {
    if (buildingDefaultStartTime) {
      return buildingDefaultStartTime;
    }
    const now = new Date();
    const nextHour = now.getHours() + 1;
    if (nextHour > 18 || nextHour < 8) {
      return '09:00';
    }
    return `${nextHour.toString().padStart(2, '0')}:00`;
  }, [buildingDefaultStartTime]);

  const { floors, visitTypes, passEntryOptions, defaultPassEntryOption } = useBuildingData();
  const hasSelectableFloors = useMemo(() => getFloorSuiteOptions(floors).length > 0, [floors]);

  // VIS-1787: resolve the seeded defaults for picker-less fields (STOP_AT_DESK /
  // BYPASS_DESK check-in, default entry option) at the form-root level so they are
  // part of `initialValues`. The child components' seed effects can be defeated by
  // a same-commit `reset(initialValues)` (React batches the seed + wipe into one
  // render, so their useWatch guards never see a transition); baking the defaults
  // into `initialValues` makes every reset re-apply them instead of wiping them.
  const { availableCheckInOptions, tenantOverride } = useCheckInOptionsData();
  const defaultCheckInOptionSelector = useMemo(
    () => selectDefaultCheckInOptionByUuidSelector(buildingUuid || ''),
    [buildingUuid],
  );
  const defaultCheckInOption = useSelector(defaultCheckInOptionSelector);
  const effectiveDefaultCheckIn = tenantOverride?.defaultCheckInOption ?? defaultCheckInOption;

  // Create flow only: an edited visit that legitimately has no visit type /
  // entry option must not be silently assigned the current building defaults
  // on update (the child seed effects keep covering edit forms as before).
  const defaultVisitTypeId = useMemo(() => {
    if (!showUiMetadata || isEditMode) {
      return '';
    }
    const resolved = resolveDefaultVisitType(
      visitTypes,
      effectiveDefaultCheckIn,
      availableCheckInOptions,
      tenantOverride,
    );
    return resolved ? resolved.id.toString() : '';
  }, [showUiMetadata, isEditMode, visitTypes, effectiveDefaultCheckIn, availableCheckInOptions, tenantOverride]);

  const defaultVisitEntryOption = useMemo(() => {
    if (
      isEditMode ||
      !visitFieldsConfig.passEntryOptionEnabled ||
      !defaultPassEntryOption ||
      !passEntryOptions?.includes(defaultPassEntryOption)
    ) {
      return '';
    }
    return defaultPassEntryOption;
  }, [isEditMode, visitFieldsConfig.passEntryOptionEnabled, defaultPassEntryOption, passEntryOptions]);

  const schema = useMemo(
    () => createVisitFormV2Schema(visitFieldsConfig, includeHostStep, hasSelectableFloors),
    [visitFieldsConfig, includeHostStep, hasSelectableFloors],
  );

  const initialValues = useMemo(
    (): CreateVisitFormV2Values => ({
      visitStartDate: savedWhenData?.visitStartDate ?? today,
      visitEndDate: savedWhenData?.visitEndDate ?? savedWhenData?.visitStartDate ?? today,
      isMultiDay: savedWhenData?.isMultiDay ?? false,
      visitDates: savedWhenData?.visitDates ?? [savedWhenData?.visitStartDate ?? today],
      startTime: savedWhenData?.startTime ?? defaultStartTime,
      endTime: savedWhenData?.endTime ?? buildingDefaultEndTime ?? null,
      duration: savedWhenData?.duration ?? (buildingDefaultEndTime ? undefined : 1),
      useDuration: savedWhenData?.useDuration ?? !buildingDefaultEndTime,
      repeatType: savedWhenData?.repeatType ?? RepeatType.NONE,
      repeatEndDate: savedWhenData?.repeatEndDate ?? null,
      recurrenceDays: savedWhenData?.recurrenceDays ?? undefined,
      hostingType: savedHostData?.hostingType ?? HostingType.SELF,
      receiveInvitationCopy: savedHostData?.receiveInvitationCopy ?? true,
      receiveCheckinNotifications: savedHostData?.receiveCheckinNotifications ?? true,
      additionalOrganizers: savedHostData?.additionalOrganizers ?? '',
      floor: savedHostData?.floor ?? '',
      suite: savedHostData?.suite ?? '',
      hostEmail: savedHostData?.hostEmail ?? user?.email ?? '',
      visitorMode: isEditMode ? VisitorMode.SINGLE : savedVisitData?.visitorMode ?? VisitorMode.SINGLE,
      visitSummary: savedVisitData?.visitSummary ?? '',
      // `||` (not `??`): a saved-but-empty value must still fall back to the
      // resolved default, otherwise a reset re-wipes the seeded field (VIS-1787).
      visitTypeId: savedVisitData?.visitTypeId || defaultVisitTypeId,
      checkInInstructions: savedVisitData?.checkInInstructions ?? '',
      noteForReception: savedVisitData?.noteForReception ?? '',
      visitEntryOption: savedVisitData?.visitEntryOption || defaultVisitEntryOption,
      acknowledgmentConfirmed: false,
      email_opt_in: savedVisitData?.email_opt_in,
      sms_opt_in: savedVisitData?.sms_opt_in,
      visitors:
        savedVisitData?.visitors && savedVisitData.visitors.length > 0 ? savedVisitData.visitors : [EMPTY_VISITOR],
    }),
    [
      savedWhenData,
      today,
      defaultStartTime,
      buildingDefaultEndTime,
      savedHostData,
      user?.email,
      savedVisitData,
      isEditMode,
      defaultVisitTypeId,
      defaultVisitEntryOption,
    ],
  );

  const formMethods: UseFormReturn<CreateVisitFormV2Values> = useForm<CreateVisitFormV2Values>({
    resolver: zodResolver(schema) as Resolver<CreateVisitFormV2Values>,
    defaultValues: initialValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (editDataVersion > 0) {
      formMethods.reset(initialValues);
    }
  }, [editDataVersion, initialValues, formMethods]);

  // Building settings load asynchronously; when they arrive, re-apply defaults
  // (start/end time) to the create form if the user hasn't started editing.
  const hasAppliedBuildingDefaultsRef = useRef(false);
  useEffect(() => {
    if (isEditMode) {
      return;
    }
    if (hasAppliedBuildingDefaultsRef.current) {
      return;
    }
    if (!buildingSettings) {
      return;
    }
    hasAppliedBuildingDefaultsRef.current = true;
    if (formMethods.formState.isDirty) {
      return;
    }
    formMethods.reset(initialValues);
  }, [isEditMode, buildingSettings, formMethods, initialValues]);

  const [isSubmittingVisit, setIsSubmittingVisit] = useState(false);

  const mapOutsideVisitorForAdapter = (visitFormValues: CreateVisitFormV2Values): Partial<CreateVisitDto> => {
    const firstVisitor = visitFormValues.visitors?.[0];
    if (!firstVisitor) {
      return {};
    }

    return {
      first_name: firstVisitor.firstName,
      last_name: firstVisitor.lastName,
      phone: firstVisitor.phone || null,
      email: firstVisitor.email || null,
    };
  };

  const mapFormValuesToDto = useCallback(
    (formValues: CreateVisitFormV2Values, options?: { forceCreateShape?: boolean }): Partial<CreateVisitDto> => {
      const forceCreateShape = options?.forceCreateShape === true;
      const contextVisitData = getVisitStepData();
      const visitorsToUse = formValues.visitors;
      const useCreateVisitorFields = !isEditMode || forceCreateShape;
      const valuesForAdapter = useCreateVisitorFields ? mapOutsideVisitorForAdapter(formValues) : {};

      const latestVisitor = visitorsToUse?.[visitorsToUse.length - 1];

      // eslint-disable-next-line operator-linebreak
      const editModeFields =
        isEditMode && visitId && !forceCreateShape
          ? {
              id: visitId,
              first_name: latestVisitor?.firstName || '',
              last_name: latestVisitor?.lastName || '',
            }
          : {};

      const monthlyMode = mapRepeatTypeToMonthlyMode(formValues.repeatType);
      const selectedVisitDates = getSelectedVisitDates(formValues);
      const isMultiDaySubmit = Boolean(formValues.isMultiDay) && selectedVisitDates.length > 1;
      const primaryDate = getEarliestVisitDate(selectedVisitDates) ?? formValues.visitStartDate;
      const isRecurring = !isMultiDaySubmit && formValues.repeatType !== RepeatType.NONE;
      const repeatEndDate = formValues.repeatEndDate
        ? new Date(
            Date.UTC(
              formValues.repeatEndDate.getFullYear(),
              formValues.repeatEndDate.getMonth(),
              formValues.repeatEndDate.getDate(),
              23,
              59,
              59,
              999,
            ),
          )
        : undefined;

      return {
        arrival_date:
          primaryDate && formValues.startTime ? getDateTime(primaryDate, formValues.startTime, timeZone) : primaryDate,
        departure_date:
          primaryDate && formValues.endTime ? getDateTime(primaryDate, formValues.endTime, timeZone) : primaryDate,
        ...(isMultiDaySubmit ? { visit_dates: datesToDateOnlyStrings(selectedVisitDates) } : {}),
        ...(!visitFieldsConfig.visitDuration.hidden && formValues.useDuration && formValues.duration != null
          ? { duration: formValues.duration }
          : {}),
        // Recurrence fields are only included when the user picks a repeat
        // type other than "Does not repeat". Sending `frequency: "once"` or a
        // stray `recurrence_days`/`end_date` on a non-recurring visit causes
        // the BE to treat it as a (degenerate) recurring visit.
        ...(isRecurring
          ? {
              frequency: mapRepeatTypeToFrequency(formValues.repeatType, formValues.recurrenceDays),
              // BE contract VIS-1057: the legacy single-visit POST expects snake_case
              // `monthly_mode` (only the bulk endpoint uses camelCase `monthlyMode`).
              ...(monthlyMode ? { monthly_mode: monthlyMode } : {}),
              ...(repeatEndDate ? { end_date: repeatEndDate } : {}),
              ...(formValues.recurrenceDays ? { recurrence_days: formValues.recurrenceDays } : {}),
            }
          : {}),
        host_email: formValues.hostEmail,
        email_recipients: formValues.additionalOrganizers
          ?.split(',')
          .map((email) => email.trim())
          .filter(Boolean),
        send_invitation_to_host: formValues.receiveInvitationCopy,
        notify_on_check_in: formValues.receiveCheckinNotifications,
        floor: formValues.floor,
        suite: formValues.suite,
        email_opt_in: formValues.email_opt_in ?? false,
        sms_opt_in: formValues.sms_opt_in ?? false,
        visitors:
          visitorsToUse?.map((visitor: Visitor) => {
            const { firstName, lastName, companyName, photoUuid, id: _omitVisitorId, email, phone, ...rest } = visitor;
            return {
              ...rest,
              email: email || null,
              phone: phone || null,
              first_name: firstName,
              last_name: lastName,
              company: companyName,
              photo_uuid: photoUuid || null,
            };
          }) || [],
        description: formValues.visitSummary || contextVisitData?.visitSummary || '',
        ...(showUiMetadata && formValues.visitTypeId ? { visit_type_id: formValues.visitTypeId } : {}),
        ...(visitFieldsConfig.passEntryOptionEnabled && formValues.visitEntryOption
          ? { pass_entry_option: formValues.visitEntryOption }
          : {}),
        ...(showUiMetadata && formValues.checkInInstructions
          ? { special_instruction_for_visitor: formValues.checkInInstructions }
          : {}),
        ...(showUiMetadata && formValues.noteForReception
          ? { special_instruction_for_receptionist: formValues.noteForReception }
          : {}),
        ...(visitFieldsConfig.acknowledgmentText && formValues.acknowledgmentConfirmed
          ? { acknowledgment_text: visitFieldsConfig.acknowledgmentText }
          : {}),
        ...(useCreateVisitorFields && hasCustomFields ? { custom_fields: {} } : {}),
        ...valuesForAdapter,
        ...editModeFields,
      };
    },
    [getVisitStepData, isEditMode, timeZone, visitId, showUiMetadata, visitFieldsConfig, hasCustomFields],
  );

  useEffect(() => {
    if (!isCreatingVisit && isSubmittingVisit) {
      setIsSubmittingVisit(false);
    }
  }, [isCreatingVisit, isSubmittingVisit]);

  const handleComplete = useCallback(
    (values?: CreateVisitFormV2Values) => {
      if (isSubmittingVisit || isCreatingVisit) {
        return;
      }

      const formValues = values ?? formMethods.getValues();
      const mappedValues = mapFormValuesToDto(formValues);
      const selectedVisitDates = getSelectedVisitDates(formValues);
      const isMultiDaySubmit = Boolean(formValues.isMultiDay) && selectedVisitDates.length > 1;

      const effectiveEndTime = getEffectiveEndTime(
        formValues.startTime,
        formValues.endTime,
        formValues.useDuration,
        formValues.duration,
      );

      if (isEditMode && visitId) {
        if (seriesUuid) {
          setIsSubmittingVisit(true);
          const seriesDates = selectedVisitDates.length > 0 ? selectedVisitDates : [formValues.visitStartDate];
          const contactChanged = didPrimaryVisitorEmailOrPhoneChange(
            baselinePrimaryVisitorContact,
            formValues.visitors,
          );
          const seriesPayload: UpdateVisitSeriesPayload = {
            dates: seriesDates.map((date) => ({
              arrivalTime: getDateTime(date, formValues.startTime, timeZone).toISOString(),
              departureTime: getDateTime(date, effectiveEndTime, timeZone).toISOString(),
            })),
            visitorNote: formValues.checkInInstructions || null,
            guardNote: formValues.noteForReception || null,
            visitPurpose: formValues.visitSummary || null,
            emailOptIn: formValues.email_opt_in,
            smsOptIn: formValues.sms_opt_in,
            ...(contactChanged ? { visitors: mapVisitorsToSeriesPayload(formValues.visitors) } : {}),
          };
          dispatch(
            updateVisitSeries.request({
              params: { buildingId: buildingUuid, seriesId: seriesUuid },
              payload: seriesPayload,
              originalDates: originalSeriesDates,
            }),
          );
        } else if (didPrimaryVisitorEmailOrPhoneChange(baselinePrimaryVisitorContact, formValues.visitors)) {
          setIsSubmittingVisit(true);
          const visitDto = mapFormValuesToDto(formValues, { forceCreateShape: true }) as CreateVisitDto;
          dispatch(
            cancelVisit.request({
              buildingId: buildingUuid,
              visitId,
              skipPostCancelRedirect: true,
            }),
          );
          dispatch(
            createVisit.request({
              buildingId: buildingUuid,
              visitDto,
              suppressVisitConfirmationRedirect: true,
            }),
          );
        } else if (isMultiDaySubmit) {
          const [primaryDate, ...additionalDates] = selectedVisitDates;
          const additionalOccurrences = additionalDates.map((date) => ({
            arrival_time: getDateTime(date, formValues.startTime, timeZone),
            departure_time: getDateTime(date, effectiveEndTime, timeZone),
          }));
          dispatch(
            updateVisit.request({
              params: { buildingId: buildingUuid, visitId },
              payload: {
                ...mappedValues,
                arrival_date: primaryDate
                  ? getDateTime(primaryDate, formValues.startTime, timeZone)
                  : mappedValues.arrival_date,
                additional_occurrences: additionalOccurrences,
              },
            }),
          );
        } else {
          dispatch(
            updateVisit.request({
              params: { buildingId: buildingUuid, visitId },
              payload: mappedValues,
              isActivityPane,
            }),
          );
        }
      } else {
        setIsSubmittingVisit(true);
        dispatch(createVisit.request({ buildingId: buildingUuid, visitDto: mappedValues as CreateVisitDto }));
      }
    },
    [
      isSubmittingVisit,
      isCreatingVisit,
      mapFormValuesToDto,
      formMethods,
      isEditMode,
      visitId,
      dispatch,
      buildingUuid,
      baselinePrimaryVisitorContact,
      isActivityPane,
      seriesUuid,
      originalSeriesDates,
      timeZone,
    ],
  );

  return (
    <stepperInstance.Stepper.Provider formMethods={formMethods} onComplete={handleComplete}>
      <CreateVisitFormV2Content
        Stepper={stepperInstance.Stepper}
        isSubmittingVisit={isSubmittingVisit}
        isCreatingVisit={isCreatingVisit}
        includeHostStep={includeHostStep}
      />
    </stepperInstance.Stepper.Provider>
  );
};

export const CreateVisitFormV2 = (): JSX.Element => (
  <CreateVisitFormV2Provider>
    <CreateVisitFormV2Inner />
  </CreateVisitFormV2Provider>
);
